import React, { useState, useEffect, useMemo } from "react";
import { Icon } from "@iconify/react";
import {
  startOfMonth,
  endOfQuarter,
  startOfYear,
  endOfYear,
  format,
  isValid,
  compareAsc,
  parseISO,
  startOfQuarter,
} from "date-fns";
import { toast } from "react-toastify";
import {
  Box,
  VStack,
  HStack,
  ButtonPrimary,
  ButtonPrimary1,
  ButtonSecondary,
} from "../../../components/utils";
import SearchInput from "../../../components/shared/SearchInput";
import { CollapsibleComponent } from "../../../components/shared/Collapsible";
import { formatDisplayDate } from "../../../utils/date";
import {
  AlignmentType,
  BlackScholes,
  BlackScholesGrantDetails,
  BlackScholesV2,
} from "../../../types/blackScholes";
import {
  downloadValuationAssumptionsTemplate,
  uploadValuationAssumptionsTemplate,
} from "../../../api/black-scholes";
import { Input, Label } from "../../../components/shared/InputField";

interface TimeAndPriceSegmentationProps {
  grantScopeData: BlackScholesV2 | undefined;
  onBack: () => void;
  onNext: (data: any) => void;
  setGrantScopeData?: (data: any) => void;
}

interface ValuationGroup {
  id: string; // The grouping key (original valuation date)
  valuationDate: string; // The editable valuation date
  grantDates: string[];
  grantCount: number;
  totalOptions: number;
}

export const ValuationAssumptions: React.FC<TimeAndPriceSegmentationProps> = ({
  grantScopeData,
  onBack,
  onNext,
  setGrantScopeData,
}) => {
  const [alignment, setAlignment] = useState<AlignmentType>(
    grantScopeData?.alignmentType || "month"
  );
  // Mapping: Grant Date -> Valuation Date
  const [valuationMappings, setValuationMappings] = useState<{
    [grantDate: string]: string;
  }>({});
  const [collapsedAccordions, setCollapsedAccordions] = useState<{
    [key: string]: boolean;
  }>({});
  const [searchQuery, setSearchQuery] = useState("");

  const filteredGrants = grantScopeData?.filteredGrants || [];

  const uniqueGrantDates = useMemo(
    () =>
      Array.from(
        new Set(
          filteredGrants
            .map((g: any) => {
              if (!g.dateOfGrant) return null;
              const d = new Date(g.dateOfGrant);
              return isValid(d) ? format(d, "yyyy-MM-dd") : null;
            })
            .filter((d: any) => d)
        )
      ).sort() as string[],
    [filteredGrants]
  );

  const getDefaultValuationDate = (dateStr: string, align: AlignmentType) => {
    const date = new Date(dateStr);
    let valDate = date;
    if (align === "custom") {
      return dateStr;
    }
    switch (align) {
      case "month":
        valDate = startOfMonth(date);
        break;
      case "quarter":
        valDate = startOfQuarter(date);
        break;
      case "year":
        valDate = startOfYear(date);
        break;
      case "halfYear": {
        const month = date.getMonth();
        const year = date.getFullYear();
        valDate = month < 6 ? new Date(year, 0, 1) : new Date(year, 6, 1);
        break;
      }
      default:
        valDate = date;
    }
    return format(valDate, "yyyy-MM-dd");
  };

  const applyAlignment = (align: AlignmentType) => {
    if (align === "custom") {
      return;
    }
    const newMapping: { [key: string]: string } = {};
    uniqueGrantDates.forEach((gd) => {
      newMapping[gd] = getDefaultValuationDate(gd, align);
    });
    setValuationMappings(newMapping);
    setCollapsedAccordions({});
  };

  // Initial Load / Restore
  useEffect(() => {
    const savedData = grantScopeData?.grantDatesWithData;
    const hasSavedData = savedData && Object.keys(savedData).length > 0;

    if (hasSavedData) {
      const newMapping: { [key: string]: string } = {};
      uniqueGrantDates.forEach((gd) => {
        const entry = savedData[gd];
        if (
          entry &&
          typeof entry === "object" &&
          "valuationDate" in entry &&
          entry.valuationDate
        ) {
          newMapping[gd] = format(new Date(entry.valuationDate), "yyyy-MM-dd");
        } else {
          // Fallback
          newMapping[gd] = getDefaultValuationDate(gd, "month");
        }
      });
      setValuationMappings(newMapping);
      // If we have saved data, we try to infer alignment or just leave it as default.
      // Since we don't store alignment, we don't set it here, but the view will be correct.
      if (grantScopeData?.alignmentType) {
        setAlignment(grantScopeData.alignmentType);
      }
    } else {
      // Initialize with default alignment
      applyAlignment("month");
    }
  }, [grantScopeData]);

  const handleAlignmentChange = (type: AlignmentType) => {
    setAlignment(type);
    applyAlignment(type);
  };

  const handleSeparate = (grantDate: string) => {
    setAlignment("custom");
    setValuationMappings((prev) => ({
      ...prev,
      [grantDate]: grantDate, // Set valuation date to grant date
    }));
  };

  const handleValuationDateChange = (
    currentValDate: string,
    newValDate: string
  ) => {
    setAlignment("custom");
    setValuationMappings((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((gd) => {
        if (next[gd] === currentValDate) {
          next[gd] = newValDate;
        }
      });
      return next;
    });
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await downloadValuationAssumptionsTemplate({
        grantIds: filteredGrants.map((g) => g.id),
      });
      const blob = new Blob([response], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "valuation_assumptions_template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (error) {
      toast.error("Error downloading template");
    }
  };

  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileName = file.name;
    const fileExtension = fileName.split(".").pop()?.toLowerCase();

    if (fileExtension !== "xlsx") {
      toast.error("Please upload a valid Excel file (.xlsx)");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setIsUploading(true);
    setUploadErrors([]);

    try {
      const newMappings = await uploadValuationAssumptionsTemplate(
        file,
        filteredGrants.map((g) => g.id)
      );

      if (newMappings.errors && newMappings.errors.length > 0) {
        setUploadErrors(newMappings.errors);
      } else if (newMappings.mapping) {
        setValuationMappings({
          ...newMappings.mapping,
        });
        setAlignment("custom");
        toast.success("Valuation dates updated successfully");
      }
    } catch (error) {
      toast.error("Failed to upload valuation assumptions");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const groups = useMemo(() => {
    const grouped: { [valDate: string]: string[] } = {};

    uniqueGrantDates.forEach((gd) => {
      const valDate = valuationMappings[gd];
      if (!valDate) return;

      if (!grouped[valDate]) {
        grouped[valDate] = [];
      }
      grouped[valDate].push(gd);
    });

    return Object.entries(grouped)
      .map(([valDate, gds]) => {
        const grantsInGroup = filteredGrants.filter((g: any) => {
          if (!g.dateOfGrant) return false;
          const gd = format(new Date(g.dateOfGrant), "yyyy-MM-dd");
          return gds.includes(gd);
        });

        const count = grantsInGroup.length;
        const totalOptions = grantsInGroup.reduce(
          (sum: number, g: any) => sum + (Number(g.grantedOptions) || 0),
          0
        );

        return {
          id: valDate, // The ID is the valuation date itself
          valuationDate: valDate,
          grantDates: gds.sort(),
          grantCount: count,
          totalOptions,
        };
      })
      .sort((a, b) => a.valuationDate.localeCompare(b.valuationDate));
  }, [uniqueGrantDates, valuationMappings, filteredGrants]);

  const displayGroups = useMemo(() => {
    if (!searchQuery.trim()) return groups;
    const query = searchQuery.toLowerCase();

    return groups
      .map((group) => {
        const groupDateStr = formatDisplayDate(
          group.valuationDate
        ).toLowerCase();
        const groupMatches = groupDateStr.includes(query);

        // Filter grant dates within the group
        const matchingGrantDates = group.grantDates.filter((gd) => {
          const grantDateStr = formatDisplayDate(gd).toLowerCase();
          if (grantDateStr.includes(query)) return true;

          // Check grants within this date
          const grants = filteredGrants.filter((g: any) => {
            if (!g.dateOfGrant) return false;
            return format(new Date(g.dateOfGrant), "yyyy-MM-dd") === gd;
          });
          return grants.some((g: any) => {
            const gid = g.grantId || g.id || "";
            return String(gid).toLowerCase().includes(query);
          });
        });

        if (groupMatches) {
          return group;
        }

        if (matchingGrantDates.length > 0) {
          const grantsInGroup = filteredGrants.filter((g: any) => {
            if (!g.dateOfGrant) return false;
            const gd = format(new Date(g.dateOfGrant), "yyyy-MM-dd");
            return matchingGrantDates.includes(gd);
          });

          const count = grantsInGroup.length;
          const totalOptions = grantsInGroup.reduce(
            (sum: number, g: any) => sum + (Number(g.grantedOptions) || 0),
            0
          );

          return {
            ...group,
            grantDates: matchingGrantDates,
            grantCount: count,
            totalOptions,
          };
        }

        return null;
      })
      .filter(Boolean) as ValuationGroup[];
  }, [groups, searchQuery, filteredGrants]);

  const toggleAccordion = (id: string) => {
    setCollapsedAccordions((prev) => ({
      ...prev,
      [id]: !(prev[id] ?? true),
    }));
  };

  const handleSubmit = () => {
    const grantDatesWithData: {
      [key: string]: BlackScholes;
    } = {};

    // We iterate through the mappings to build the result
    Object.entries(valuationMappings).forEach(([grantDate, valDate]) => {
      grantDatesWithData[grantDate] = {
        dividendYield:
          grantScopeData?.grantDatesWithData?.[grantDate]?.dividendYield || 0,
        riskFreeRate:
          grantScopeData?.grantDatesWithData?.[grantDate]?.riskFreeRate || 0,
        stockPrice:
          grantScopeData?.grantDatesWithData?.[grantDate]?.stockPrice || 0,
        volatility:
          grantScopeData?.grantDatesWithData?.[grantDate]?.volatility || 0,
        valuationDate: new Date(valDate),
      };
    });

    const updatedData: BlackScholesV2 = {
      ...grantScopeData!,
      grantDatesWithData,
      alignmentType: alignment,
    };

    if (setGrantScopeData) {
      setGrantScopeData(updatedData);
    }
    onNext(updatedData);
  };

  return (
    <Box className="w-full">
      <VStack className="">
        <VStack className="justify-between gap-1 pb-3 pl-6 font-medium border-b-[0.5px] pt-7">
          <HStack className="flex justify-between font-semibold text-lg1 text-black-501">
            Valuation Assumptions
          </HStack>
          <p className="text-gray-401 font-medium text-sm3">
            Define valuation dates for each grant group
          </p>
        </VStack>

        <VStack className="gap-6 w-full p-6">
          <Box className="bg-blue-50 p-4 rounded-lg flex items-start gap-3 border border-blue-100">
            <Icon
              icon="mdi:information"
              className="text-blue-600 mt-0.5 shrink-0"
              width={20}
            />
            <div className="text-sm text-blue-900">
              <p className="font-medium mb-1">About Valuation Dates</p>
              <p>
                By default, grants are grouped by month with the valuation date
                set to the start of the month. You can customize this grouping
                or set specific dates for individual groups.
              </p>
            </div>
          </Box>

          {uploadErrors.length > 0 && (
            <div className="p-2 mt-2 overflow-auto border border-red-600 bg-[#c728280d] rounded max-h-48">
              <HStack className="justify-between items-start">
                <HStack className="gap-2">
                  <Icon
                    icon={"material-symbols:error"}
                    color="#c72828"
                    height={20}
                  />
                  <p className="font-medium text-red-600">
                    {uploadErrors.length} Errors Found
                  </p>
                </HStack>
                <button
                  onClick={() => setUploadErrors([])}
                  className="text-red-600 hover:text-red-800 transition-colors"
                  title="Clear errors"
                >
                  <Icon icon="mdi:close" height={20} />
                </button>
              </HStack>
              <ul className="text-red-600 list-disc pl-5 mt-1">
                {uploadErrors.map((err, index) => (
                  <li className="text-xs" key={index}>
                    {err}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <HStack className="gap-6 w-full items-end justify-between">
            <Box className="flex-1 max-w-sm">
              <SearchInput
                placeholder="Search Grants"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Box>

            <HStack className="gap-6 items-end">
              <Box className="min-w-[200px]">
                <Label className="text-sm font-normal mb-1">
                  Alignment Preference
                </Label>
                <div className="relative">
                  <select
                    value={alignment}
                    onChange={(e) =>
                      handleAlignmentChange(e.target.value as AlignmentType)
                    }
                    className="w-full h-10 px-3 bg-white border border-gray-300 rounded text-sm focus:border-orange-501 focus:ring-1 focus:ring-orange-501 outline-hidden appearance-none cursor-pointer"
                  >
                    <option value="unique">Unique (Exact Date)</option>
                    <option value="month">Start of Month</option>
                    <option value="quarter">Start of Quarter</option>
                    <option value="halfYear">Start of Half Year</option>
                    <option value="year">Start of Year</option>
                    <option value="custom">Custom</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none text-gray-500">
                    <Icon icon="mdi:chevron-down" width={20} />
                  </div>
                </div>
              </Box>

              <Box className="flex gap-3 pb-0.5">
                <ButtonPrimary1
                  onClick={handleDownloadTemplate}
                  className="whitespace-nowrap flex items-center gap-2"
                >
                  <Icon icon="mdi:download" height={18} />
                  Download
                </ButtonPrimary1>
                <ButtonPrimary1
                  onClick={handleUploadClick}
                  disabled={isUploading}
                  className="whitespace-nowrap flex items-center gap-2"
                >
                  <Icon icon="mdi:upload" height={18} />
                  {isUploading ? "Uploading..." : "Upload"}
                </ButtonPrimary1>
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept=".xlsx"
                  onChange={handleFileChange}
                />
              </Box>
            </HStack>
          </HStack>

          <VStack className="gap-4">
            {displayGroups.map((group) => (
              <CollapsibleComponent
                key={group.id}
                label={
                  <Box>
                    <HStack className="gap-2 items-center">
                      <span className="font-medium">
                        Valuation Date: {formatDisplayDate(group.valuationDate)}
                      </span>
                      <span className="text-sm text-gray-500">
                        ({group.grantCount} Grants,{" "}
                        {group.totalOptions.toLocaleString()} Options)
                      </span>
                    </HStack>
                    <p className="text-xs text-gray-400 mt-1">
                      {group.grantDates.length} Grant Dates
                    </p>
                  </Box>
                }
                labelDetail={``}
                isOpen={collapsedAccordions[group.id] ?? true}
                onClick={() => toggleAccordion(group.id)}
              >
                <VStack className="gap-4 py-4">
                  <Box className="w-full max-w-xs hidden">
                    <Label className="text-xs text-gray-500 mb-1">
                      Valuation Date
                    </Label>
                    <Input
                      type="date"
                      value={group.valuationDate}
                      onChange={(e) =>
                        handleValuationDateChange(group.id, e.target.value)
                      }
                    />
                  </Box>

                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Grant Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Grants
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {group.grantDates.map((gd) => {
                          const count = filteredGrants.filter((g: any) => {
                            if (!g.dateOfGrant) return false;
                            return (
                              format(new Date(g.dateOfGrant), "yyyy-MM-dd") ===
                              gd
                            );
                          }).length;
                          // Check if this grant date is already its own valuation date (separated)
                          const isSeparated = valuationMappings[gd] === gd;

                          return (
                            <tr key={gd}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatDisplayDate(gd)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {count}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                {alignment !== "unique" && !isSeparated && (
                                  <button
                                    onClick={() => handleSeparate(gd)}
                                    className="text-blue-600 hover:text-blue-900"
                                  >
                                    Separate
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </VStack>
              </CollapsibleComponent>
            ))}
          </VStack>

          <HStack className="justify-between pt-4 border-t border-gray-200 mt-4">
            <ButtonPrimary1 onClick={onBack}>
              Back to Grant Scope
            </ButtonPrimary1>
            <ButtonPrimary onClick={() => handleSubmit()}>
              Next: Other Assumptions
            </ButtonPrimary>
          </HStack>
        </VStack>
      </VStack>
    </Box>
  );
};
